import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import api from '../../api';
import { usePermissions } from '../../hooks/usePermissions';
import { Layout } from '../../components/layout/Layout';
import { AssignedUserSelect } from '../../components/AssignedUserSelect';
import ExportButton from '../../components/ExportButton';
import { Checkbox } from '../../components/ui/checkbox';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import BulkEditBar from '../../components/BulkEditBar';
import { 
  Smartphone, Plus, Search, Eye, Edit, Trash2, Copy, X,
  Phone, User, Building2, MapPin, ChevronUp, ChevronDown, ChevronsUpDown, Upload, Download
} from 'lucide-react';

const MobileDevices = () => {
  const navigate = useNavigate();
  const { canCreate, canUpdate, canDelete } = usePermissions('micro', 'mobile_devices');
  
  // Data states
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ 
    total: 0, 
    active: 0, 
    inactive: 0, 
    sin_estado: 0,
    by_os: [],
    by_brand: []
  });
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [sortConfig, setSortConfig] = useState({ field: null, dir: 'asc' });
  
  // Filter states
  const [filters, setFilters] = useState({ search: '', brand_id: '', status_id: '', os_id: '', site_id: '', cliente_id: '', imei: '', plan: '', user_name: '', modelo_filter: '', phone_filter: '', legajo_filter: '', location_filter: '' });
  const [filterOptions, setFilterOptions] = useState({ brands: [], statuses: [], os: [], sites: [], users: [] });
  const [formOptions, setFormOptions] = useState({ empresas: [], sites: [], brands: [], statuses: [], os: [], ubicaciones: [], proveedores: [], usuarios: [], clientes: [] });
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [formData, setFormData] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [activeStatFilter, setActiveStatFilter] = useState(null);
  
  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  // Bulk selection
  const { 
    selectedIds, selectedCount, isAllSelected, isSomeSelected, 
    isSelected, toggleRow, toggleAll, clearSelection 
  } = useBulkSelection(devices);

  // Fetch functions
  const fetchStats = async () => {
    try {
      const response = await api.get('/assets/mobile-devices/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/assets/mobile-devices/filter/options');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchFormOptions = async () => {
    try {
      const response = await api.get('/assets/mobile-devices/form/options');
      setFormOptions(response.data);
    } catch (error) {
      console.error('Error fetching form options:', error);
    }
  };

  const fetchDevices = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);
      
      if (filters.search) params.append('search', filters.search);
      if (filters.brand_id) params.append('brand_id', filters.brand_id);
      if (filters.status_id) params.append('status_id', filters.status_id);
      if (filters.os_id) params.append('os_id', filters.os_id);
      if (filters.site_id) params.append('site_id', filters.site_id);
      if (filters.imei) params.append('imei', filters.imei);
      if (filters.plan) params.append('plan', filters.plan);
      if (filters.user_name) params.append('user_name', filters.user_name);
      if (filters.cliente_id) params.append('cliente_id', filters.cliente_id);
      if (filters.modelo_filter) params.append('modelo_filter', filters.modelo_filter);
      if (filters.phone_filter) params.append('phone_filter', filters.phone_filter);
      if (filters.legajo_filter) params.append('legajo_filter', filters.legajo_filter);
      if (filters.location_filter) params.append('location_filter', filters.location_filter);
      if (sortConfig.field) {
        params.append('sort_by', sortConfig.field);
        params.append('sort_dir', sortConfig.dir);
      }
      
      const response = await api.get(`/assets/mobile-devices?${params.toString()}`);
      setDevices(response.data.data);
      setPagination(prev => ({ 
        ...prev, 
        total: response.data.total, 
        pages: response.data.pages 
      }));
    } catch (error) {
      console.error('Error fetching devices:', error);
      toast.error('Error al cargar celulares');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchFilterOptions();
    fetchFormOptions();
  }, []);

  useEffect(() => {
    fetchDevices();
  }, [pagination.page, pagination.limit, sortConfig]);

  // Debounced filter fetch (800ms)
  useEffect(() => {
    const t = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchDevices();
    }, 800);
    return () => clearTimeout(t);
  }, [filters]);

  // Handlers
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ search: '', brand_id: '', status_id: '', os_id: '', site_id: '', cliente_id: '', imei: '', plan: '', user_name: '', modelo_filter: '', phone_filter: '', legajo_filter: '', location_filter: '' });
    setActiveStatFilter(null);
  };

  const hasActiveFilters = Object.values(filters).some(v => v && v !== '');

  const handleStatClick = (filterType, value) => {
    if (activeStatFilter === filterType) {
      clearFilters();
    } else {
      setActiveStatFilter(filterType);
      if (filterType === 'total') {
        clearFilters();
      } else if (filterType === 'active') {
        setFilters(prev => ({ ...prev, status_id: '6' }));
      } else if (filterType === 'inactive') {
        setFilters(prev => ({ ...prev, status_id: '7' }));
      } else if (filterType === 'sin_estado') {
        setFilters(prev => ({ ...prev, status_id: 'null' }));
      }
    }
  };

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  const openCreateModal = () => {
    setModalMode('create');
    setFormData({});
    setShowModal(true);
  };

  const openEditModal = (device) => {
    setModalMode('edit');
    setSelectedDevice(device);
    setFormData({
      brand_id: device.brand_id || '',
      model: device.model || '',
      os_id: device.os_id || '',
      imei: device.imei || '',
      serial_number: device.serial_number || '',
      phone_number: device.phone_number || '',
      status_id: device.status_id || '',
      assigned_user_id: device.assigned_user_id || '',
      empresa_id: device.empresa_id || '',
      site_id: device.site_id || '',
      location_id: device.location_id || '',
      supplier_id: device.supplier_id || '',
      cliente_id: device.cliente_id || '',
      purchase_date: device.purchase_date || '',
      comments: device.comments || ''
    });
    setShowModal(true);
  };

  const openCopyModal = (device) => {
    setModalMode('create');
    setFormData({
      brand_id: device.brand_id || '',
      model: device.model || '',
      os_id: device.os_id || '',
      status_id: device.status_id || '',
      empresa_id: device.empresa_id || '',
      site_id: device.site_id || '',
      location_id: device.location_id || '',
      supplier_id: device.supplier_id || ''
      // Los datos de identificación y asignación se completan para el nuevo registro.
    });
    setShowModal(true);
    toast.info('Datos copiados - Completá el IMEI y revisá los datos que correspondan.');
  };

  const handleSubmit = async () => {
    if (!formData.imei) {
      toast.error('El IMEI es requerido');
      return;
    }
    if (!formData.model) {
      toast.error('El Modelo es requerido');
      return;
    }

    try {
      if (modalMode === 'create') {
        await api.post('/assets/mobile-devices', formData);
        toast.success('Celular creado exitosamente');
      } else {
        await api.put(`/assets/mobile-devices/${selectedDevice.id}`, formData);
        toast.success('Celular actualizado exitosamente');
      }
      setShowModal(false);
      fetchDevices();
      fetchStats();
      fetchFilterOptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleUserCreated = (createdUser) => {
    setFormOptions(prev => ({
      ...prev,
      usuarios: [...(prev.usuarios || []), createdUser]
        .sort((a, b) => (a.name || a.nombre || '').localeCompare(b.name || b.nombre || '', 'es')),
    }));
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/assets/mobile-devices/${deviceToDelete.id}`);
      toast.success('Celular eliminado exitosamente');
      setShowDeleteModal(false);
      setDeviceToDelete(null);
      fetchDevices();
      fetchStats();
      fetchFilterOptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  // Import Excel handler
  const handleImport = async () => {
    if (!importFile) {
      toast.error('Selecciona un archivo Excel');
      return;
    }
    
    setImporting(true);
    setImportResults(null);
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await api.post('/assets/mobile-devices/import/excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResults(response.data);
      toast.success(`Importación completada: ${response.data.imported} registros importados`);
      fetchDevices();
      fetchStats();
      fetchFilterOptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error en la importación');
    } finally {
      setImporting(false);
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
    setImportResults(null);
  };

  const getStatusBadge = (status) => {
    if (!status) return <span className="badge badge-default">Sin estado</span>;
    if (status.toLowerCase().includes('inactivo')) return <span className="badge badge-red">{status}</span>;
    if (status.toLowerCase().includes('activo')) return <span className="badge badge-green">{status}</span>;
    return <span className="badge badge-default">{status}</span>;
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Smartphone className="h-8 w-8 text-primary" strokeWidth={1.5} />
            <div>
              <h1 className="text-2xl font-semibold">Celulares</h1>
              <p className="text-sm text-muted-foreground">Gestión de dispositivos móviles corporativos</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ExportButton 
              endpoint="/assets/mobile-devices/export/excel"
              filters={filters}
              filename="celulares"
            />
            {canCreate && (
              <Button variant="outline" onClick={() => setShowImportModal(true)} data-testid="import-btn">
                <Upload className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Importar
              </Button>
            )}
            {canCreate && (
              <Button onClick={openCreateModal} data-testid="new-device-btn">
                <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Nuevo Celular
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="mobile-stats">
          {/* Total */}
          <div 
            className={`stats-card cursor-pointer transition-all ${activeStatFilter === 'total' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleStatClick('total')}
            data-testid="stats-total"
          >
            <div className="mb-2">
              <Smartphone className="h-8 w-8 text-primary" strokeWidth={1.5} />
            </div>
            <div className="stats-card-value">{stats.total}</div>
            <div className="stats-card-label">Total</div>
          </div>
          
          {/* Activos */}
          <div 
            className={`stats-card cursor-pointer transition-all ${activeStatFilter === 'active' ? 'ring-2 ring-green-500' : ''}`}
            onClick={() => handleStatClick('active')}
            data-testid="stats-active"
          >
            <div className="mb-2">
              <Phone className="h-8 w-8 text-green-500" strokeWidth={1.5} />
            </div>
            <div className="stats-card-value text-green-500">{stats.active}</div>
            <div className="stats-card-label">Activos</div>
          </div>
          
          {/* Inactivos */}
          <div 
            className={`stats-card cursor-pointer transition-all ${activeStatFilter === 'inactive' ? 'ring-2 ring-red-500' : ''}`}
            onClick={() => handleStatClick('inactive')}
            data-testid="stats-inactive"
          >
            <div className="mb-2">
              <Phone className="h-8 w-8 text-red-500" strokeWidth={1.5} />
            </div>
            <div className="stats-card-value text-red-500">{stats.inactive}</div>
            <div className="stats-card-label">Inactivos</div>
          </div>
          
          {/* Sin Estado */}
          <div 
            className={`stats-card cursor-pointer transition-all ${activeStatFilter === 'sin_estado' ? 'ring-2 ring-yellow-500' : ''}`}
            onClick={() => handleStatClick('sin_estado')}
            data-testid="stats-sin-estado"
          >
            <div className="mb-2">
              <Phone className="h-8 w-8 text-yellow-500" strokeWidth={1.5} />
            </div>
            <div className="stats-card-value text-yellow-500">{stats.sin_estado}</div>
            <div className="stats-card-label">Sin Estado</div>
          </div>
          
          {/* Top Marca */}
          <div className="stats-card flex items-center gap-3" data-testid="stats-brand">
            <Building2 className="h-6 w-6 text-purple-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-purple-500">
              {stats.by_brand?.[0]?.count || 0}
            </div>
              <div className="stats-card-label">{stats.by_brand?.[0]?.name || 'Sin Marca'}</div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <Card className="card-subtle">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <Input
                  placeholder="Buscar en todos los campos..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10"
                  data-testid="global-search-input"
                />
              </div>
              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                  <X className="h-4 w-4 mr-1" strokeWidth={1.5} />
                  Limpiar filtros
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="card-subtle">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="mobile-devices-table">
                <thead>
                  <tr className="border-b border-border">
                    <th className="w-10 py-3 px-4">
                      <Checkbox
                        checked={isAllSelected}
                        indeterminate={isSomeSelected}
                        onCheckedChange={toggleAll}
                        aria-label="Seleccionar todos"
                      />
                    </th>
                    <th 
                      className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground"
                      onClick={() => handleSort('imei')}
                    >
                      <div className="flex items-center gap-1">
                        IMEI <SortIcon field="imei" />
                      </div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">MARCA</th>
                    <th 
                      className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground"
                      onClick={() => handleSort('model')}
                    >
                      <div className="flex items-center gap-1">
                        MODELO <SortIcon field="model" />
                      </div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden lg:table-cell cursor-pointer hover:text-foreground" onClick={() => handleSort('cliente_name')}>
                      <div className="flex items-center gap-1">CLIENTE <SortIcon field="cliente_name" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('phone_number')}>
                      <div className="flex items-center gap-1">TELÉFONO <SortIcon field="phone_number" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden xl:table-cell cursor-pointer hover:text-foreground" onClick={() => handleSort('plan')}>
                      <div className="flex items-center gap-1">PLAN <SortIcon field="plan" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('status_name')}>
                      <div className="flex items-center gap-1">ESTADO <SortIcon field="status_name" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden lg:table-cell cursor-pointer hover:text-foreground" onClick={() => handleSort('user_name')}>
                      <div className="flex items-center gap-1">USUARIO <SortIcon field="user_name" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden lg:table-cell cursor-pointer hover:text-foreground" onClick={() => handleSort('usuario_legajo')}>
                      <div className="flex items-center gap-1">LEGAJO <SortIcon field="usuario_legajo" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden lg:table-cell cursor-pointer hover:text-foreground" onClick={() => handleSort('site_name')}>
                      <div className="flex items-center gap-1">SITE <SortIcon field="site_name" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden lg:table-cell cursor-pointer hover:text-foreground" onClick={() => handleSort('location_name')}>
                      <div className="flex items-center gap-1">UBICACIÓN <SortIcon field="location_name" /></div>
                    </th>
                    <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground w-28">ACCIONES</th>
                  </tr>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="w-10 py-2 px-4"></th>
                    <th className="py-2 px-4">
                      <Input placeholder="Filtrar..." className="h-7 text-xs bg-background" value={filters.imei} onChange={(e) => handleFilterChange('imei', e.target.value)} />
                    </th>
                    <th className="py-2 px-4">
                      <Select value={filters.brand_id || undefined} onValueChange={(v) => handleFilterChange('brand_id', v === 'todos' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos</SelectItem>
                          {filterOptions.brands?.map(b => (<SelectItem key={b.id} value={String(b.id)}>{b.nombre}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </th>
                    <th className="py-2 px-4">
                      <Input placeholder="Filtrar..." className="h-7 text-xs bg-background" value={filters.modelo_filter} onChange={(e) => handleFilterChange('modelo_filter', e.target.value)} />
                    </th>
                    <th className="py-2 px-4 hidden lg:table-cell">
                      <Select value={filters.cliente_id || undefined} onValueChange={(v) => handleFilterChange('cliente_id', v === 'todos' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos</SelectItem>
                          {formOptions.clientes?.map(c => (<SelectItem key={c.id} value={String(c.id)}>{c.nombre}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </th>
                    <th className="py-2 px-4">
                      <Input placeholder="Filtrar..." className="h-7 text-xs bg-background" value={filters.phone_filter} onChange={(e) => handleFilterChange('phone_filter', e.target.value)} />
                    </th>
                    <th className="py-2 px-4 hidden xl:table-cell">
                      <Input placeholder="Filtrar..." className="h-7 text-xs bg-background" value={filters.plan} onChange={(e) => handleFilterChange('plan', e.target.value)} />
                    </th>
                    <th className="py-2 px-4">
                      <Select value={filters.status_id || undefined} onValueChange={(v) => handleFilterChange('status_id', v === 'todos' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos</SelectItem>
                          {filterOptions.statuses?.map(s => (<SelectItem key={s.id} value={String(s.id)}>{s.nombre}</SelectItem>))}
                          <SelectItem value="null">Sin estado</SelectItem>
                        </SelectContent>
                      </Select>
                    </th>
                    <th className="py-2 px-4 hidden lg:table-cell">
                      <Input placeholder="Filtrar..." className="h-7 text-xs bg-background" value={filters.user_name} onChange={(e) => handleFilterChange('user_name', e.target.value)} />
                    </th>
                    <th className="py-2 px-4 hidden lg:table-cell">
                      <Input placeholder="Filtrar..." className="h-7 text-xs bg-background" value={filters.legajo_filter} onChange={(e) => handleFilterChange('legajo_filter', e.target.value)} />
                    </th>
                    <th className="py-2 px-4 hidden lg:table-cell">
                      <Select value={filters.site_id || undefined} onValueChange={(v) => handleFilterChange('site_id', v === 'todos' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos</SelectItem>
                          {filterOptions.sites?.map(s => (<SelectItem key={s.id} value={String(s.id)}>{s.nombre}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </th>
                    <th className="py-2 px-4 hidden lg:table-cell">
                      <Input placeholder="Filtrar..." className="h-7 text-xs bg-background" value={filters.location_filter} onChange={(e) => handleFilterChange('location_filter', e.target.value)} />
                    </th>
                    <th className="py-2 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={13} className="text-center py-8 text-muted-foreground">
                        Cargando...
                      </td>
                    </tr>
                  ) : devices.length === 0 ? (
                    <tr>
                      <td colSpan={13} className="text-center py-8 text-muted-foreground">
                        No se encontraron celulares
                      </td>
                    </tr>
                  ) : (
                    devices.map((device) => (
                      <tr key={device.id} className={`border-b border-border hover:bg-muted/50 transition-colors ${isSelected(device.id) ? 'bg-primary/5' : ''}`} data-testid={`device-row-${device.id}`}>
                        <td className="w-10 py-3 px-4">
                          <Checkbox
                            checked={isSelected(device.id)}
                            onCheckedChange={() => toggleRow(device.id)}
                            aria-label={`Seleccionar ${device.imei}`}
                          />
                        </td>
                        <td className="py-3 px-4 font-mono text-sm">{device.imei || '-'}</td>
                        <td className="py-3 px-4">{device.brand_name || '-'}</td>
                        <td className="py-3 px-4">{device.model || '-'}</td>
                        <td className="py-3 px-4 hidden lg:table-cell">{device.cliente_name || '-'}</td>
                        <td className="py-3 px-4 font-mono text-sm">{device.phone_number || '-'}</td>
                        <td className="py-3 px-4 hidden xl:table-cell text-sm">{device.plan || '-'}</td>
                        <td className="py-3 px-4">{getStatusBadge(device.status_name)}</td>
                        <td className="py-3 px-4 hidden lg:table-cell">
                          {device.user_name ? (
                            <div>
                              <div className="text-sm">{device.user_name}</div>
                              {device.user_department && (
                                <div className="text-xs text-muted-foreground">{device.user_department}</div>
                              )}
                            </div>
                          ) : '-'}
                        </td>
                        <td className="py-3 px-4 hidden lg:table-cell">{device.usuario_legajo || '-'}</td>
                        <td className="py-3 px-4 hidden lg:table-cell">{device.site_name || '-'}</td>
                        <td className="py-3 px-4 hidden lg:table-cell">{device.location_name || '-'}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => navigate(`/micro/mobile-devices/${device.id}`)}
                              title="Ver detalle"
                              data-testid={`view-btn-${device.id}`}
                            >
                              <Eye className="h-4 w-4" strokeWidth={1.5} />
                            </Button>
                            {canUpdate && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openEditModal(device)}
                                title="Editar"
                                data-testid={`edit-btn-${device.id}`}
                              >
                                <Edit className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                            )}
                            {canCreate && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openCopyModal(device)}
                                title="Copiar"
                                data-testid={`copy-btn-${device.id}`}
                              >
                                <Copy className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                onClick={() => { setDeviceToDelete(device); setShowDeleteModal(true); }}
                                title="Eliminar"
                                data-testid={`delete-btn-${device.id}`}
                              >
                                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {pagination.total > 0 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <div className="flex items-center gap-4">
                  <p className="text-sm text-muted-foreground">
                    Mostrando {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Mostrar:</span>
                    <Select
                      value={pagination.limit.toString()}
                      onValueChange={(value) => setPagination(p => ({ ...p, limit: parseInt(value), page: 1 }))}
                    >
                      <SelectTrigger className="h-8 w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page === 1}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page === pagination.pages}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Smartphone className="h-5 w-5" strokeWidth={1.5} />
                {modalMode === 'create' ? 'Nuevo Celular' : 'Editar Celular'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="grid grid-cols-2 gap-4 py-4">
              {/* Campos únicos - borde amarillo en modo crear */}
              <div className="space-y-2">
                <Label>IMEI *</Label>
                <Input
                  value={formData.imei || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, imei: e.target.value }))}
                  placeholder="Ej: 353456789012345"
                  className={modalMode === 'create' && !formData.imei ? 'border-yellow-500' : ''}
                  data-testid="imei-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Número de serie (opcional)</Label>
                <Input
                  value={formData.serial_number || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, serial_number: e.target.value }))}
                  data-testid="serial-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Marca</Label>
                <Select value={String(formData.brand_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, brand_id: v }))}>
                  <SelectTrigger className={modalMode === 'create' && formData.brand_id ? 'border-green-500' : ''}>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {formOptions.brands?.map(b => (
                      <SelectItem key={b.id} value={String(b.id)}>{b.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Modelo *</Label>
                <Input
                  value={formData.model || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                  placeholder="Ej: iPhone 15 Pro, Galaxy S24"
                  className={modalMode === 'create' && formData.model ? 'border-green-500' : ''}
                  data-testid="model-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Sistema Operativo</Label>
                <Select value={String(formData.os_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, os_id: v }))}>
                  <SelectTrigger className={modalMode === 'create' && formData.os_id ? 'border-green-500' : ''}>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {formOptions.os?.map(o => (
                      <SelectItem key={o.id} value={String(o.id)}>{o.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input
                  value={formData.phone_number || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone_number: e.target.value }))}
                  placeholder="Ej: +54 11 1234-5678"
                  className={modalMode === 'create' && !formData.phone_number ? 'border-yellow-500' : ''}
                  data-testid="phone-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Plan</Label>
                <Input
                  value={formData.plan || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, plan: e.target.value }))}
                  placeholder="Ej: Plan Corporativo, Plan Básico"
                  data-testid="plan-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select value={String(formData.status_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, status_id: v }))}>
                  <SelectTrigger className={modalMode === 'create' && formData.status_id ? 'border-green-500' : ''}>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {formOptions.statuses?.map(s => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <AssignedUserSelect
                value={formData.assigned_user_id}
                onValueChange={(assigned_user_id) => setFormData(prev => ({ ...prev, assigned_user_id }))}
                users={formOptions.usuarios}
                onUserCreated={handleUserCreated}
              />
              
              <div className="space-y-2">
                <Label>Empresa</Label>
                <Select value={String(formData.empresa_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, empresa_id: v }))}>
                  <SelectTrigger className={modalMode === 'create' && formData.empresa_id ? 'border-green-500' : ''}>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {formOptions.empresas?.map(e => (
                      <SelectItem key={e.id} value={String(e.id)}>{e.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Site</Label>
                <Select value={String(formData.site_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, site_id: v }))}>
                  <SelectTrigger className={modalMode === 'create' && formData.site_id ? 'border-green-500' : ''}>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {formOptions.sites?.map(s => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Ubicación</Label>
                <Select value={String(formData.location_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, location_id: v }))}>
                  <SelectTrigger className={modalMode === 'create' && formData.location_id ? 'border-green-500' : ''}>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {formOptions.ubicaciones?.map(u => (
                      <SelectItem key={u.id} value={String(u.id)}>{u.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Proveedor</Label>
                <Select value={String(formData.supplier_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, supplier_id: v }))}>
                  <SelectTrigger className={modalMode === 'create' && formData.supplier_id ? 'border-green-500' : ''}>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {formOptions.proveedores?.map(p => (
                      <SelectItem key={p.id} value={String(p.id)}>{p.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Cliente</Label>
                <Select value={String(formData.cliente_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, cliente_id: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {formOptions.clientes?.map(c => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Fecha de Compra</Label>
                <Input
                  type="date"
                  value={formData.purchase_date || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, purchase_date: e.target.value }))}
                  data-testid="purchase-date-input"
                />
              </div>
              
              <div className="col-span-2 space-y-2">
                <Label>Comentarios</Label>
                <Input
                  value={formData.comments || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
                  placeholder="Observaciones adicionales..."
                  data-testid="comments-input"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSubmit} data-testid="submit-btn">
                {modalMode === 'create' ? 'Crear' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" strokeWidth={1.5} />
                Eliminar Celular
              </DialogTitle>
            </DialogHeader>
            <p className="py-4">
              ¿Estás seguro de que deseas eliminar el celular con IMEI <strong>{deviceToDelete?.imei}</strong>?
              <br />
              <span className="text-sm text-muted-foreground">Esta acción no se puede deshacer.</span>
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Edit Bar */}
        <BulkEditBar
          selectedIds={selectedIds}
          onClear={clearSelection}
          onSuccess={() => { fetchDevices(); fetchStats(); }}
          endpoint="/assets/mobile-devices"
          options={{
            sites: formOptions.sites,
            empresas: formOptions.empresas,
            statuses: formOptions.statuses,
            ubicaciones: formOptions.ubicaciones
          }}
          fields={['site_id', 'empresa_id', 'status_id', 'location_id']}
        />

        {/* Import Modal */}
        <Dialog open={showImportModal} onOpenChange={closeImportModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5 text-primary" strokeWidth={1.5} />
                Importar Celulares desde Excel
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {!importing && !importResults && (
                <>
                  {/* Drag & Drop Zone */}
                  <div
                    className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer
                      ${importFile 
                        ? 'border-primary bg-primary/5' 
                        : 'border-muted-foreground/30 hover:border-primary/50 hover:bg-muted/50'
                      }`}
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-primary', 'bg-primary/10'); }}
                    onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-primary', 'bg-primary/10'); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-primary', 'bg-primary/10');
                      const file = e.dataTransfer.files[0];
                      if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
                        setImportFile(file);
                      }
                    }}
                    onClick={() => document.getElementById('import-file-input').click()}
                  >
                    <input
                      id="import-file-input"
                      type="file"
                      accept=".xlsx,.xls"
                      className="hidden"
                      onChange={(e) => setImportFile(e.target.files[0])}
                      data-testid="import-file-input"
                    />
                    {importFile ? (
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                          <Smartphone className="h-6 w-6 text-green-500" />
                        </div>
                        <p className="font-medium text-sm">{importFile.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(importFile.size / 1024).toFixed(1)} KB
                        </p>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={(e) => { e.stopPropagation(); setImportFile(null); }}
                          className="text-xs"
                        >
                          <X className="h-3 w-3 mr-1" /> Quitar
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                          <Upload className="h-6 w-6 text-muted-foreground" />
                        </div>
                        <p className="font-medium text-sm">Arrastrá tu archivo aquí</p>
                        <p className="text-xs text-muted-foreground">o hacé click para seleccionar</p>
                        <p className="text-xs text-muted-foreground/70">Solo archivos .xlsx o .xls</p>
                      </div>
                    )}
                  </div>

                  {/* Download Template Button */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <div className="flex items-center gap-2">
                      <Download className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">¿No tenés el formato?</span>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={async () => {
                        try {
                          const response = await api.get('/assets/mobile-devices/import/template', { responseType: 'blob' });
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', 'plantilla_celulares.xlsx');
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                        } catch (err) {
                          toast.error('Error al descargar plantilla');
                        }
                      }}
                    >
                      Descargar plantilla
                    </Button>
                  </div>
                </>
              )}

              {/* Progress Animation */}
              {importing && (
                <div className="py-8 space-y-4">
                  <div className="flex justify-center">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center animate-pulse">
                      <Upload className="h-8 w-8 text-primary animate-bounce" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-primary rounded-full animate-progress" 
                           style={{ animation: 'progress 2s ease-in-out infinite' }} />
                    </div>
                    <p className="text-center text-sm text-muted-foreground">Procesando archivo...</p>
                  </div>
                  <style>{`
                    @keyframes progress {
                      0% { width: 0%; margin-left: 0%; }
                      50% { width: 60%; margin-left: 20%; }
                      100% { width: 0%; margin-left: 100%; }
                    }
                  `}</style>
                </div>
              )}
              
              {/* Results Cards */}
              {importResults && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20 text-center">
                      <div className="text-2xl font-bold text-green-500">{importResults.imported}</div>
                      <div className="text-xs text-green-600">Importados</div>
                    </div>
                    <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20 text-center">
                      <div className="text-2xl font-bold text-blue-500">{importResults.usuarios_created}</div>
                      <div className="text-xs text-blue-600">Usuarios nuevos</div>
                    </div>
                  </div>
                  
                  {/* Duplicates info */}
                  {importResults.duplicates > 0 && (
                    <details className="group">
                      <summary className="flex items-center justify-between p-3 rounded-lg bg-slate-500/10 cursor-pointer list-none">
                        <span className="text-sm font-medium text-slate-500 flex items-center gap-2">
                          <Phone className="h-4 w-4" />
                          {importResults.duplicates} líneas ya existían
                        </span>
                        <ChevronDown className="h-4 w-4 text-slate-500 transition-transform group-open:rotate-180" />
                      </summary>
                      <ul className="mt-2 p-3 rounded-lg bg-muted/50 text-xs space-y-1 max-h-24 overflow-y-auto">
                        {importResults.duplicate_lines?.slice(0, 20).map((line, i) => (
                          <li key={i} className="text-muted-foreground font-mono">{line}</li>
                        ))}
                        {importResults.duplicate_lines?.length > 20 && (
                          <li className="text-muted-foreground">... y {importResults.duplicate_lines.length - 20} más</li>
                        )}
                      </ul>
                    </details>
                  )}
                  
                  {/* Real errors */}
                  {importResults.errors?.length > 0 && (
                    <details className="group">
                      <summary className="flex items-center justify-between p-3 rounded-lg bg-destructive/10 cursor-pointer list-none">
                        <span className="text-sm font-medium text-destructive flex items-center gap-2">
                          <X className="h-4 w-4" />
                          {importResults.errors.length} errores encontrados
                        </span>
                        <ChevronDown className="h-4 w-4 text-destructive transition-transform group-open:rotate-180" />
                      </summary>
                      <ul className="mt-2 p-3 rounded-lg bg-muted/50 text-xs space-y-1 max-h-32 overflow-y-auto">
                        {importResults.errors.map((err, i) => (
                          <li key={i} className="text-muted-foreground">{err}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={closeImportModal}>
                {importResults ? 'Cerrar' : 'Cancelar'}
              </Button>
              {!importResults && !importing && (
                <Button onClick={handleImport} disabled={!importFile} data-testid="confirm-import-btn">
                  <Upload className="h-4 w-4 mr-2" />
                  Importar
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default MobileDevices;
